import { useEffect, useRef, useState } from 'react';
import type { SpaceListItem } from '../api/auth';
import type { TaskUser } from '../api/tasks';

export const SPACE_COLORS = ['#8e44ad', '#0071e3', '#e67e22', '#16a085', '#c0392b', '#2c3e50'];
export function spaceColor(id: number): string {
  return SPACE_COLORS[id % SPACE_COLORS.length] ?? '#0071e3';
}

const AVATAR_COLORS = [
  '#e8384f', '#fd612c', '#fd9a00', '#eec300', '#a4cf30', '#62d26f',
  '#37c5ab', '#20aaea', '#4186e0', '#7a6ff0', '#aa62e3', '#e25fda', '#ec5f8b',
];
export function avatarColor(id: number | null | undefined): string {
  if (id == null) return '#c7c7cc';
  const i = ((id % AVATAR_COLORS.length) + AVATAR_COLORS.length) % AVATAR_COLORS.length;
  return AVATAR_COLORS[i]!;
}

export function initials(name: string): string {
  const p = name.trim().split(/\s+/);
  return ((p[0]?.[0] ?? '') + (p[1]?.[0] ?? '')).toUpperCase() || '?';
}

export function Avatar({
  name,
  userId,
  size = 28,
  title,
  avatarUrl,
}: {
  name: string;
  userId?: number | null;
  size?: number;
  title?: string;
  avatarUrl?: string | null;
}) {
  const [imgFailed, setImgFailed] = useState(false);
  const tt = title ?? (name && name !== '-' ? name : undefined);
  if (avatarUrl && !imgFailed) {
    return (
      <img
        className="asn-avatar asn-avatar-img"
        src={avatarUrl}
        alt={name}
        title={tt}
        style={{ width: size, height: size }}
        onError={() => setImgFailed(true)}
      />
    );
  }
  const empty = userId == null && (!name || name === '-' || name === '?');
  return (
    <span
      className={`asn-avatar${empty ? ' empty' : ''}`}
      title={tt}
      style={{
        width: size,
        height: size,
        fontSize: Math.round(size * 0.4),
        background: empty ? undefined : avatarColor(userId),
      }}
    >
      {empty ? '' : initials(name)}
    </span>
  );
}

export function userLabel(u: TaskUser): string {
  return [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || `#${u.id}`;
}

// Custom dropdown pro výběr přiřazeného uživatele (Asana-style): v seznamu je
// přímo fotka/kolečko u jména, ne nativní <select> (ten obrázky neumí).
export function AssigneePicker({
  users,
  value,
  onChange,
}: {
  users: TaskUser[];
  value: number | null;
  onChange: (id: number | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const sel = value != null ? users.find((u) => u.id === value) : undefined;
  const selName = sel ? userLabel(sel) : 'Nepřiřazeno';

  return (
    <div className={`asn-assignee-picker${open ? ' open' : ''}`} ref={ref}>
      <button type="button" className="asn-assignee-trigger" onClick={() => setOpen((o) => !o)}>
        <Avatar name={selName} userId={value} avatarUrl={sel?.avatarUrl ?? null} size={22} />
        <span className="asn-assignee-label">{selName}</span>
        <svg className="asn-assignee-caret" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
      </button>
      {open && (
        <div className="asn-assignee-menu">
          <button type="button" className={`asn-assignee-option${value == null ? ' active' : ''}`} onClick={() => { onChange(null); setOpen(false); }}>
            <Avatar name="-" userId={null} size={22} />
            <span>Nepřiřazeno</span>
          </button>
          {users.map((u) => (
            <button
              key={u.id}
              type="button"
              className={`asn-assignee-option${u.id === value ? ' active' : ''}`}
              onClick={() => { onChange(u.id); setOpen(false); }}
            >
              <Avatar name={userLabel(u)} userId={u.id} avatarUrl={u.avatarUrl} size={22} />
              <span>{userLabel(u)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function SpaceIcon({ space, size }: { space: SpaceListItem | undefined; size?: number }) {
  const [failed, setFailed] = useState(false);
  const style = size ? { width: size, height: size } : undefined;
  if (space && !failed) {
    return (
      <img
        className="asn-space-icon-lg"
        src={`/assets/spaces/${space.slug}.png`}
        alt={space.name}
        style={{ objectFit: 'cover', ...style }}
        onError={() => setFailed(true)}
      />
    );
  }
  if (space) {
    return (
      <span className="asn-space-icon-lg" style={{ background: spaceColor(space.id), fontSize: '0.82rem', fontWeight: 700, ...style }}>
        {space.name.charAt(0).toUpperCase()}
      </span>
    );
  }
  return (
    <span className="asn-space-icon-lg" style={{ background: '#8e8e93', ...style }}>
      <span className="feather-icon"><i data-feather="layers"></i></span>
    </span>
  );
}

export const MAX_ATTACH_BYTES = 25 * 1024 * 1024;

export function formatSize(bytes: number | null): string {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function fileToBase64(file: File): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
